import DataTable from "@/components/tables/DataTable";
import { TableHeaderType, TableProps } from "@/components/tables/types";
import { format } from "date-fns";
import { Stack, LoadingButton } from "@altimateai/lego";
import { Typography, Input, Checkbox } from "@altimateai/lego";
import { Popconfirm } from "@altimateai/lego";
import { IconSearch, IconTrash } from "@tabler/icons-react";
import { Memory } from "./types";
import { RelativeComponentLoader } from "@/components/loader";
import { BackButton } from "@/components/button";
import { useUsers, useDebounce } from "@/helpers/hooks";
import { Link } from "react-router-dom";
import { FilterApplicableFor } from "@/components/tags/types";
import { useMemo, useState } from "react";
import { DismissibleAlertBanner } from "@/components/banners/DismissibleAlertBanner";
import DeleteMemoryButton from "./DeleteMemoryButton";
import useMemories from "./useMemories";
import EditMemory from "./EditMemory";
import useBulkDeleteMemories from "./useBulkDeleteMemories";
import { useBreadcrumb } from "@/components/breadcrumbs";

const MCPMemories = () => {
  useBreadcrumb("Memory Hub", { defaultParents: [{ label: "Datamates", pathname: "/datamates" }] });
  const [search, setSearch] = useState("");
  const debouncedSearchRaw = useDebounce(search, 300);
  const debouncedSearch = typeof debouncedSearchRaw === "string" ? debouncedSearchRaw : undefined;
  const { getByUuid } = useUsers(true);
  const { datamates, isFetching, datamatesLoading, memories } = useMemories({ debouncedSearch });

  const datamatesById = useMemo(
    () =>
      datamates?.reduce(
        (acc, datamate) => {
          if (datamate.id) {
            acc[datamate.id] = datamate;
          }
          return acc;
        },
        {} as Record<string, (typeof datamates)[0]>
      ),
    [datamates]
  );

  const {
    selectedMemoryIds,
    isAllSelected,
    isIndeterminate,
    handleSelectAll,
    handleSelectMemory,
    handleBulkDelete,
    bulkDeleteMutation,
    canUserDeleteMemory,
  } = useBulkDeleteMemories({ memories, datamatesById });

  // add tags, created by columns
  const headers: TableHeaderType<Memory>[] = [
    {
      id: 0,
      uid: "select",
      label: (
        <Checkbox
          checked={isIndeterminate ? "indeterminate" : isAllSelected}
          onCheckedChange={handleSelectAll}
        />
      ),
      width: 50,
      render: row => {
        if (!canUserDeleteMemory(row)) return null;

        return (
          <Checkbox
            checked={selectedMemoryIds.includes(row.id)}
            onCheckedChange={(checked: boolean) => handleSelectMemory(row.id, checked)}
          />
        );
      },
    },
    {
      id: 1,
      uid: "memory",
      label: "Memory",
      width: "30%",
      render: row => (
        <Link to={`/datamates/memories/${row.id}`}>{row.metadata?.title || row.memory}</Link>
      ),
    },
    {
      id: 7,
      uid: "datamates",
      label: "Datamates",
      width: 100,
      render: row => (
        <Link to={`/datamate/${row.metadata?.datamate_id}`}>{row.metadata?.datamate_name}</Link>
      ),
    },
    {
      id: 8,
      uid: "visibility",
      label: "Visibility",
      width: 100,
      render: row => (
        <span className="al-capitalize">
          {datamatesById?.[row.metadata?.datamate_id || ""]?.privacy || "private"}
        </span>
      ),
    },
    {
      id: 4,
      uid: "created_by",
      label: "Created By",
      width: 100,
      render: row => {
        const user = getByUuid(row.user_id);
        if (!user) return "";
        return `${user.first_name} ${user.last_name}`;
      },
    },
    {
      id: 5,
      uid: "updated_at",
      label: "Last modified",
      width: 100,
      render: row => format(new Date(row.updated_at), "MMM dd, yyyy HH:mm"),
    },
    {
      id: 6,
      uid: "actions",
      label: "Actions",
      width: 100,
      render: row => {
        const datamate = datamatesById?.[row.metadata?.datamate_id || ""];
        if (!datamate) return null;

        return (
          <Stack justify="start" align="center">
            <EditMemory memory={row} datamate={datamate} />
            <DeleteMemoryButton memory={row} />
          </Stack>
        );
      },
    },
  ];

  const tableProps: TableProps<Memory> = {
    header: headers,
    data: { items: memories },
    headerClass: "al-bg-gray-50",
    tableClass: "al-min-w-full",
    tableBodyClass: "al-bg-white",
  };

  return (
    <div className="al-p-6">
      <div className="al-mb-6">
        <Stack align="center">
          <BackButton defaultUrl="/datamates" />
          <Typography variant="page-heading">Datamates Memories</Typography>
        </Stack>
        <div className="al-mt-4">
          <DismissibleAlertBanner
            bannerId="mcp-memories"
            content="Memory gives an Agent the ability to recall relevant information. Capture every coding insight, architectural decision, and tribal knowledge with our intelligent memory management system"
          />
        </div>
      </div>

      <div>
        <Stack justify="between" align="center" className="al-mb-4">
          <div className="al-relative al-w-64">
            <Input
              placeholder="Search memories..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="al-pl-9"
            />
            <IconSearch className="al-absolute al-left-3 al-top-[4px] al-translate-y-1/2 al-h-4 al-w-4 al-text-gray-500 al-pointer-events-none" />
          </div>
          <Stack align="center" gap="md">
            {selectedMemoryIds.length > 0 && (
              <Popconfirm
                title="Delete selected memories"
                description={`Are you sure you want to delete ${selectedMemoryIds.length} selected memories?`}
                onConfirm={handleBulkDelete}
              >
                <LoadingButton
                  variant="destructive"
                  size="sm"
                  loading={bulkDeleteMutation.isPending}
                >
                  <IconTrash className="al-w-4 al-h-4 al-mr-2" />
                  Bulk Delete ({selectedMemoryIds.length})
                </LoadingButton>
              </Popconfirm>
            )}
            <div id="column-picker-portal" />
          </Stack>
        </Stack>
        {(datamatesLoading || isFetching) && (
          <RelativeComponentLoader label="Loading..." componentHeight={10} />
        )}
        <>
          {!datamatesLoading && !isFetching && memories.length === 0 && (
            <Stack
              direction="column"
              justify="center"
              align="center"
              className="al-p-8 al-bg-white al-rounded-lg al-border al-border-gray-200"
            >
              <p className="al-text-gray-500 al-text-center">
                No memories have been added yet. Memories will be automatically added when you
                interact with the LLM using the Datamates.
              </p>
            </Stack>
          )}

          {search || memories.length > 0 ? (
            <DataTable
              {...tableProps}
              columnPickerPortal="column-picker-portal"
              applicableFor={FilterApplicableFor.MCP_MEMORIES}
            />
          ) : null}
        </>
      </div>
    </div>
  );
};

export default MCPMemories;
